/**
 * views/ProfileView.js
 * Propósito: Formulario de perfil (nombre/email) y cambio de contraseña.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { getProfile, updateProfile, changePassword } from '../services/api.js';
import AuthModal from '../components/AuthModal.js';

export default function ProfileView() {
  const { user, token, updateUser, refreshUser } = useAuth();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirm: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Cargar perfil al entrar
  useEffect(() => {
    if (!token) {
      setShowAuthModal(true);
      setLoadingProfile(false);
      return;
    }
    (async () => {
      try {
        const res = await getProfile(token);
        setForm({ name: res.user.name, email: res.user.email, phone: res.user.phone || '' });
      } catch (err) {
        setError(err.message || 'No se pudo cargar el perfil');
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [token]);

  const onChangeField = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  async function handleSaveProfile(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.name || !form.email) {
      setError('Nombre y email son requeridos');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await updateProfile({ name: form.name, email: form.email, phone: form.phone }, token);
      updateUser(res.user);
      setMessage('Perfil actualizado');
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el perfil');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!pwForm.oldPassword || !pwForm.newPassword || !pwForm.confirm) {
      setError('Todos los campos de contraseña son requeridos');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirm) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    setSavingPassword(true);
    try {
      await changePassword({ oldPassword: pwForm.oldPassword, newPassword: pwForm.newPassword }, token);
      setMessage('Contraseña actualizada');
      setPwForm({ oldPassword: '', newPassword: '', confirm: '' });
      await refreshUser();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar la contraseña');
    } finally {
      setSavingPassword(false);
    }
  }

  if (loadingProfile) {
    return <div style={{ color: '#fff', textAlign: 'center', padding: '2rem' }}>Cargando perfil...</div>;
  }

  if (!token) {
    return (
      <>
        <div className="alert alert-info mt-3">Debes iniciar sesión para ver tu perfil.</div>
        {showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
      </>
    );
  }

  return (
    <>
      <div className="card" style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '15px',
        marginTop: '1rem'
      }}>
        <div className="card-body">
          <h3 style={{ color: '#fff' }}>Perfil de usuario</h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Actualiza tu información y gestiona tu contraseña.
          </p>

          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          <div className="row g-4">
            <div className="col-md-6">
              <h5 style={{ color: '#fff' }}>Datos de perfil</h5>
              <form onSubmit={handleSaveProfile}>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Nombre</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={onChangeField}
                    disabled={savingProfile}
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={onChangeField}
                    disabled={savingProfile}
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Teléfono (opcional)</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={form.phone}
                    onChange={onChangeField}
                    disabled={savingProfile}
                    placeholder="+48 600 000 000"
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={savingProfile}>
                  {savingProfile ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </form>
            </div>

            <div className="col-md-6">
              <h5 style={{ color: '#fff' }}>Cambiar contraseña</h5>
              <form onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Contraseña actual</label>
                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.oldPassword}
                    onChange={(e) => setPwForm({ ...pwForm, oldPassword: e.target.value })}
                    disabled={savingPassword}
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Nueva contraseña</label>
                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.newPassword}
                    onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
                    disabled={savingPassword}
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ color: '#fff' }}>Confirmar nueva contraseña</label>
                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                    disabled={savingPassword}
                    style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                  />
                </div>
                <button type="submit" className="btn btn-outline-light w-100" disabled={savingPassword}>
                  {savingPassword ? 'Actualizando...' : 'Cambiar contraseña'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showAuthModal && (
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      )}
    </>
  );
}
